import * as React from 'react'

import { Box, Grid, Spinner, Title } from '@factorialco/deprecated-design-system'
import {
  InfojobsOfferDocument,
  JobPostingDataDocument,
  JobQuestionsFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useQuery } from 'modules/core/hooks/useQuery'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import {
  AtsIntegrations,
  PROMOTION_PROVIDERS,
  PromotionProviderInfo,
} from 'modules/ats/pages/JobPostingPromotion/utils'
import { Promotions } from 'modules/ats/store/collections/promotions'
import type Job from 'modules/ats/store/models/Job'

import { Infojobs } from './Providers/Infojobs'

interface Props {
  job: Job
  installedIntegrations: AtsIntegrations
}

export const JobBoards = observer(({ job, installedIntegrations }: Props) => {
  const promotions = useLazyRef(() => new Promotions()).current

  const { loading } = useAsyncData(
    async () => promotions.fetch({ data: { ats_job_posting_id: job.id } }),
    [job.id]
  )

  const { data: jobData } = useQuery(JobPostingDataDocument, {
    variables: { jobId: job.id },
  })

  const pollingInterval = 30000
  const { data, stopPolling, startPolling } = useQuery(InfojobsOfferDocument, {
    variables: { atsJobPostingId: job.id },
    pollInterval: pollingInterval,
    onCompleted: async (newData) => {
      const offer = newData?.integrationsInfojobs.offersConnection.nodes[0]
      const inProgress = offer?.status === 'in_progress'

      if (inProgress) {
        startPolling(pollingInterval)
        return
      }
      stopPolling()
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  })

  const offerCount = data?.integrationsInfojobs.offersConnection.nodes.length ?? 0
  const infojobsOffer = data?.integrationsInfojobs.offersConnection.nodes[offerCount - 1]
  const jobQuestions = jobData?.ats.jobPostingsConnection.nodes[0]?.questionsConnection.nodes || []

  const infojobsQuestions: JobQuestionsFragment[] = jobQuestions.filter(
    (question) => question.autoDisqualify === true
  )
  const locationData = jobData?.ats.jobPostingsConnection.nodes[0]?.location

  const companyInstalledIntegrations = Object.keys(installedIntegrations)

  if (loading)
    return (
      <Box gap="s16" alignItems="center">
        <Spinner />
      </Box>
    )

  const jobAttributes = {
    city: locationData?.city,
    zip: locationData?.postalCode,
    description: job.attributes.description,
    title: job.attributes.title,
    id: job.id,
    questions: infojobsQuestions,
  }

  return (
    <Box gap="s24">
      <Title
        variant="500"
        title={i18n.t('ats.job_details.promotion.job_boards.title')}
        illustration="network-group"
      />
      <Grid variant="large">
        <Infojobs
          offer={infojobsOffer}
          installed={companyInstalledIntegrations.includes('infojobs')}
          jobAttributes={jobAttributes}
        />
        {PROMOTION_PROVIDERS.map((info: PromotionProviderInfo) => {
          if (info.feature && !hasFeature(info.feature)) return null

          return React.createElement(info.component, {
            key: info.provider,
            job: job,
            promotions: promotions,
          })
        })}
      </Grid>
    </Box>
  )
})
